/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import toast from "react-hot-toast";
import {
  closeConnectAdasProgramWizard, Experiment, selectConnectAdasProgramWizard,
  selectCurrentStudyId, selectExperiment, selectStudy, Study
} from "../../../../../../redux/ducks/SensingSystemArchitectureDesign";
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks";
import ArgosDialog from "../../../../../components/Dialogs/ArgosDialog";
import { SelectProgramStep } from './SelectProgramStep/SelectProgramStep';
import { usePostRequest } from "../../../../../../api/helper";
import { SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS } from "../../../constants";
import BoxWithCenteredContent from "../../../../../components/Containers/BoxWithCenteredContent";
import ArgosButton from "../../../../../components/Buttons/ArgosButton";
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox";
import React from "react";
import {EResetSensorsToBomRequestType} from "../../ExperimentsPanel/BillOfMaterialsSection/BillOfMaterialsSection";


/**
 * Allows the user to connect a Sensing System Architecture Design study to an
 * ADAS program.
 */
export const ConnectAdasProgramWizard = () => {
  const dispatch = useAppDispatch()

  const isOpen = useAppSelector(selectConnectAdasProgramWizard).isOpen
  const formData = useAppSelector(selectConnectAdasProgramWizard).data
  const closeDialog = () => {
    dispatch(closeConnectAdasProgramWizard())
  }
  const currentStudyId = useAppSelector(selectCurrentStudyId)
  const study = useAppSelector(state => selectStudy(state, currentStudyId ?? ''))
  const experiment = useAppSelector(selectExperiment)
  const adasProgramId = formData?.adasProgramId

  const editStudyUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.landing.endpoints.editStudy
  const editStudyHandle = usePostRequest(editStudyUrl)

  const onFinish = (study: Study, experiment: Experiment) => {
    closeDialog()
    const updatedStudy = {...study, adasProgramId}
    editStudyHandle(updatedStudy).catch((err) => {
      console.log(err)
      toast.error("An error occurred while editing the study")
    })
    handleImportSensors()
  }

  const importSensorsUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.importSensors;
  const importSensorsHandle = usePostRequest(importSensorsUrl);
  const handleImportSensors = () => {
    const body = {adasProgramId, experimentId: experiment?.id, studyId: currentStudyId, requestType: EResetSensorsToBomRequestType.RESET_ALL};
    toast.loading("Importing sensors...", {id: "import-sensors-toast"});
    importSensorsHandle(body).catch((err) => {
      console.log(err);
      toast.error("An error occurred while importing sensors");
    }).finally(() => {
      toast.dismiss("import-sensors-toast");
    });
  };

  return (
    <ArgosDialog
      open={isOpen}
      headerTitle='Connect ADAS Program'
      height='80vh'
      width='60vw'
    >
      <ArgosFlexbox vertical noGap style={{overflow: 'auto', height: '100%'}}>
          <div style={{flex: 1, overflow: 'auto'}}>
            <SelectProgramStep />
          </div>
          <BoxWithCenteredContent horizontal style={{marginTop: 15}}>
            <ArgosButton
              onClick={() => {
                closeDialog()
              }}
            >
              CANCEL
            </ArgosButton>
            <ArgosButton
              onClick={() => {
                if (study !== undefined && experiment !== undefined) { onFinish(study, experiment)}
              }}
            >
              OK
            </ArgosButton>
          </BoxWithCenteredContent>
        </ArgosFlexbox>
    </ArgosDialog>
  )
}
